import { useMutation, useQuery } from '@tanstack/react-query';
import { atomWithQuery } from 'jotai-tanstack-query';
import { atomFamily } from 'jotai-family';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { WaterfallConfigPayload } from '../../../data-models/fund-waterfall.data-model';
import { queryClient } from '../../../services/MaggieQueryClient';
import {
  getFundWaterfall,
  getFundWaterfallConfig,
  getFundWaterfallResults,
  updateFundWaterfallConfig,
} from '../../../services/queries/MaggieFundQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { getForesightStore } from '../../../util/jotai-store';
import { selectedDateStringFPState } from '../state/FPState';

type WaterfallData3Params = {
  fundId: number;
  asOfDate: string;
};

export const waterfallDataAtom = atomFamily(
  ({ fundId, asOfDate }: WaterfallData3Params) =>
    atomWithQuery(() => ({
      queryKey: ['waterfallData3', { fundId, asOfDate }],
      queryFn: () => getFundWaterfall(fundId, asOfDate),
    })),
  (a, b) => {
    return a.fundId === b.fundId && a.asOfDate === b.asOfDate;
  }
);

export function invalidateWaterfallData3({ fundId, asOfDate }: WaterfallData3Params) {
  return queryClient.invalidateQueries({ queryKey: ['waterfallData3', { fundId, asOfDate }] });
}

/******/
/* TODO: check if we even need these - config & results will be returned as part of FundWaterfallResponse when calling useWaterfallData3;
It will be hard to keep all those overlapping queries in sync, so prob. better to use just one,
unless we need separate component updates for config & results to avoid unnecessary re-renders.
*/
export function useWaterfallConfig(fundId: number) {
  return useQuery({
    queryKey: ['waterfallConfig', fundId],
    queryFn: () => getFundWaterfallConfig(fundId),
  });
}

export function invalidateWaterfallConfig(fundId: number) {
  return queryClient.invalidateQueries({ queryKey: ['waterfallConfig', fundId] });
}

export function useWaterfallResults({ fundId, asOfDate }: WaterfallData3Params) {
  return useQuery({
    queryKey: ['waterfallResults', { fundId, asOfDate }],
    queryFn: () => getFundWaterfallResults(fundId, asOfDate),
  });
}

export function invalidateWaterfallResults({ fundId, asOfDate }: WaterfallData3Params) {
  return queryClient.invalidateQueries({ queryKey: ['waterfallResults', { fundId, asOfDate }] });
}
/******/

export function useUpdateWaterfallConfig() {
  const { pushErrorToast } = useToastMessageState();

  return useMutation({
    mutationFn: async (payload: WaterfallConfigPayload) => {
      const res = await updateFundWaterfallConfig(payload.fundId, payload);
      // The save is not done until the refetched config has landed: the next save builds its
      // payload from it, and letting the refetch run loose puts a read on the wire beside the
      // write that triggered it.
      await invalidateWaterfallData3({
        fundId: payload.fundId,
        asOfDate: getForesightStore().get(selectedDateStringFPState),
      });
      return res;
    },
    onError: (error, _payload) => {
      // non-validation errors
      console.error(error);
      pushErrorToast({ message: getErrorMessage(error, 'Failed to update waterfall config') });
    },
  });
}
